import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import CaseRecommendationIcon from '@cp/src/assets/icons/CaseRecommendationIcon';
import Typography from '@navi/web-ui/lib/primitives/Typography/Typography';
import cx from 'classnames';
import { openRecommendedModal, closeRecommendedModal } from '../reducer';
import { RootState } from '@cp/src/store';
import RecommendedModal from './RecommendedModal';
import {
  fetchCaseRecommendationCases,
  fetchCaseRecommendationMetadata
} from '../caseRecommendationActions';
import {
  DEFAULT_LIMIT_FOR_RECOMMENDED_CASES,
  DEFAULT_OFFSET_FOR_RECOMMENDED_CASES
} from '../utils';
import { RecommendedListBannerProps } from '../types';
import { addClickstreamEvent } from '@cp/src/service/clickStreamEventService';
import { CLICKSTREAM_EVENT_NAMES } from '@cp/src/service/clickStream.constant';
import AnimatedBorderBox from '@cp/src/components/AnimatedBorderBox/AnimatedBorderBox';

const RecommendedListBanner: React.FC<RecommendedListBannerProps> = ({
  primaryText,
  secondaryText,
  className,
  onBeforeModalOpen,
  imageWidth,
  imageHeight,
  clickstreamEventName,
  clickstreamEventDescription
}) => {
  const dispatch = useDispatch();
  const { isModalOpen } = useSelector((state: RootState) => state.caseRecommendation);
  const { caseList } = useSelector((state: RootState) => state.caseRecommendation.recommendedCases);

  const handleBannerClick = () => {
    if (clickstreamEventName) {
      addClickstreamEvent({
        name: clickstreamEventName as keyof typeof CLICKSTREAM_EVENT_NAMES,
        description: clickstreamEventDescription
      });
    }
    dispatch(fetchCaseRecommendationMetadata());

    onBeforeModalOpen?.();
    dispatch(openRecommendedModal());

    if (!caseList?.length) {
      dispatch(
        fetchCaseRecommendationCases({
          limit: DEFAULT_LIMIT_FOR_RECOMMENDED_CASES,
          offset: DEFAULT_OFFSET_FOR_RECOMMENDED_CASES
        })
      );
    }
  };

  const handleCloseModal = () => {
    dispatch(closeRecommendedModal());
  };

  return (
    <>
      <div
        className="w-full relative cursor-pointer hover:opacity-90 transition-opacity"
        onClick={handleBannerClick}
      >
        <AnimatedBorderBox
          borderRadius={12}
          strokeWidth={1}
          animationDuration={1200}
          gradientStartColor="var(--blue-base)"
          gradientEndColor="var(--blue-border)"
          gradientId="recommendedBannerGradient"
          className="w-full"
        >
          <div
            className={cx(
              'flex flex-row items-center gap-2 bg-gradient-to-r from-[var(--blue-bg)] to-[var(--bg-primary)] rounded-xl cursor-pointer px-3',
              className
            )}
          >
            <CaseRecommendationIcon width={imageWidth} height={imageHeight} />
            <div className="flex flex-col py-2">
              <Typography variant="h4" color="var(--blue-dark)" className="font-extrabold">
                {primaryText}
              </Typography>
              {secondaryText && (
                <Typography variant="p4" color="var(--grayscale-2)">
                  {secondaryText}
                </Typography>
              )}
            </div>
          </div>
        </AnimatedBorderBox>
      </div>
      <RecommendedModal isOpen={isModalOpen} onClose={handleCloseModal} />
    </>
  );
};

export default RecommendedListBanner;
